<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ isEdit ? '編輯集章活動' : '新增集章活動' }}</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" @click="handleCancel">
          {{ isEdit ? '返回' : '取消' }}
        </button>
        <button
          v-if="!isEdit || canSaveCurrentTab"
          type="button"
          class="btn btn-primary"
          :disabled="submitting"
          @click="handleSubmit"
        >
          <span v-if="submitting"><i class="ri-loader-4-line spin"></i> 儲存中...</span>
          <span v-else><i class="ri-save-line"></i> {{ isEdit ? '更新活動' : '建立活動' }}</span>
        </button>
      </div>
    </div>

    <div class="page-container">
    <div v-if="isEdit" class="tab-nav">
      <button
        type="button"
        class="tab-btn"
        :class="{ active: activeTab === 'basic' }"
        @click="activeTab = 'basic'"
      >
        <i class="ri-settings-3-line"></i> 基本設定
      </button>
      <button
        type="button"
        class="tab-btn"
        :class="{ active: activeTab === 'rewards' }"
        @click="activeTab = 'rewards'"
      >
        <i class="ri-gift-line"></i> 達成目標設定
      </button>
      <button
        type="button"
        class="tab-btn"
        :class="{ active: activeTab === 'issue' }"
        @click="activeTab = 'issue'"
      >
        <i class="ri-qr-code-line"></i> 補章／QR Code
      </button>
      <button
        type="button"
        class="tab-btn"
        :class="{ active: activeTab === 'stats' }"
        @click="activeTab = 'stats'"
      >
        <i class="ri-bar-chart-line"></i> 活動成效
      </button>
    </div>

    <div class="form-container">
      <form novalidate @submit.prevent="handleSubmit">
        <div v-show="!isEdit || activeTab === 'basic'" class="tab-pane">
        <div class="form-section">
          <h3 class="section-title">基本資訊</h3>

          <div class="form-row">
            <div class="form-group">
              <label>活動代碼</label>
              <input
                v-model="form.campaignCode"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.campaignCode }"
                placeholder="留空將自動生成"
                :disabled="isEdit"
              />
              <div v-if="errors.campaignCode" class="invalid-feedback">
                {{ errors.campaignCode }}
              </div>
            </div>
            <div class="form-group">
              <label class="required">活動名稱</label>
              <input
                v-model="form.campaignName"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.campaignName }"
                placeholder="例如：滿額集章回饋活動"
              />
              <div v-if="errors.campaignName" class="invalid-feedback">
                {{ errors.campaignName }}
              </div>
            </div>
          </div>

          <div class="form-group">
            <label>活動說明</label>
            <textarea
              v-model="form.description"
              class="form-control"
              rows="4"
              placeholder="請輸入活動說明"
            ></textarea>
          </div>

          <div class="form-group">
            <label>活動圖片</label>
            <ImageUploader
              v-model="form.imageUrl"
              :uploadFn="uploadImage"
              shape="card"
              hint="建議尺寸 1280 x 720"
            />
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>開始時間</label>
              <input
                v-model="form.startAt"
                type="datetime-local"
                class="form-control"
              />
            </div>
            <div class="form-group">
              <label>結束時間</label>
              <input
                v-model="form.endAt"
                type="datetime-local"
                class="form-control"
                :class="{ 'is-invalid': errors.endAt }"
              />
              <div v-if="errors.endAt" class="invalid-feedback">
                {{ errors.endAt }}
              </div>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label class="required">總章數</label>
              <input
                v-model.number="form.totalStamps"
                type="number"
                class="form-control"
                min="1"
                max="99"
              />
            </div>
            <div class="form-group">
              <label>啟用狀態</label>
              <div class="toggle-row">
                <label class="toggle-switch">
                  <input v-model="form.isActive" type="checkbox" />
                  <span class="toggle-slider"></span>
                </label>
                <span class="toggle-status" :class="form.isActive ? 'on' : 'off'">
                  {{ form.isActive ? '已啟用' : '已停用' }}
                </span>
              </div>
            </div>
          </div>

          <div class="form-group">
            <label>集滿章卡後</label>
            <div class="completion-mode-group">
              <button
                type="button"
                class="completion-mode-chip"
                :class="{ 'completion-mode-chip--active': form.stampCompletionMode === 'once' }"
                @click="form.stampCompletionMode = 'once'"
              >
                <i :class="form.stampCompletionMode === 'once' ? 'ri-checkbox-circle-fill' : 'ri-circle-line'"></i>
                <span class="completion-mode-title">僅集滿一次</span>
                <span class="completion-mode-desc">集滿後完成，不再發章</span>
              </button>
              <button
                type="button"
                class="completion-mode-chip"
                :class="{ 'completion-mode-chip--active': form.stampCompletionMode === 'repeat_cycle' }"
                @click="form.stampCompletionMode = 'repeat_cycle'"
              >
                <i :class="form.stampCompletionMode === 'repeat_cycle' ? 'ri-checkbox-circle-fill' : 'ri-circle-line'"></i>
                <span class="completion-mode-title">可重複集章</span>
                <span class="completion-mode-desc">集滿後重置，可再集下一輪</span>
              </button>
            </div>
          </div>
        </div>

        <div class="form-section">
          <h3 class="section-title">交易型集章規則</h3>

          <div class="form-row">
            <div class="form-group">
              <label class="required">規則類型</label>
              <select v-model="form.ruleType" class="form-control">
                <option value="order_once">每筆交易集章一次</option>
                <option value="order_amount">單筆交易滿額集章</option>
              </select>
            </div>
            <div class="form-group">
              <label class="required">每次發章數</label>
              <input
                v-model.number="form.stampCount"
                type="number"
                class="form-control"
                min="1"
                max="20"
              />
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>最低交易金額</label>
              <input
                v-model.number="form.minOrderAmount"
                type="number"
                class="form-control"
                min="0"
                step="1"
                :disabled="form.ruleType !== 'order_amount'"
                placeholder="例如：500"
              />
              <p class="form-hint">僅規則類型為「單筆交易滿額集章」時生效</p>
            </div>
            <div class="form-group">
              <label>每日個人集章上限</label>
              <input
                v-model.number="form.dailyMemberLimit"
                type="number"
                class="form-control"
                min="0"
                step="1"
                placeholder="0 表示不限"
              />
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>活動總發章上限</label>
              <input
                v-model.number="form.totalIssueLimit"
                type="number"
                class="form-control"
                min="0"
                step="1"
                placeholder="0 表示不限"
              />
              <p class="form-hint">不設限請留空或輸入 0；若要限制，請輸入大於 0 的整數（例如：1000）。</p>
            </div>
          </div>

          <div class="form-group">
            <label>指定會員等級</label>
            <div v-if="memberTiers.length === 0" class="form-hint">尚無會員等級資料，請先至會員等級管理建立。</div>
            <div v-else>
              <div class="tier-actions">
                <button
                  type="button"
                  class="btn btn-sm btn-secondary"
                  :disabled="form.memberTierCodes.length === allMemberTierCodes.length"
                  @click="selectAllTiers"
                >
                  全選
                </button>
                <button
                  type="button"
                  class="btn btn-sm btn-secondary"
                  :disabled="form.memberTierCodes.length === 0"
                  @click="clearAllTiers"
                >
                  全不選
                </button>
              </div>
              <div class="tier-chip-group">
                <button
                  v-for="tier in memberTiers"
                  :key="tier.tierCode"
                  type="button"
                  class="tier-chip"
                  :class="{ 'tier-chip--active': form.memberTierCodes.includes(tier.tierCode) }"
                  @click="toggleTier(tier.tierCode)"
                >
                  <i
                    class="tier-chip-icon"
                    :class="form.memberTierCodes.includes(tier.tierCode) ? 'ri-checkbox-circle-fill' : 'ri-circle-line'"
                  ></i>
                  {{ tier.tierName }}
                </button>
              </div>
            </div>
            <p class="form-hint">
              請至少勾選一個支援等級，預設為<strong>全部勾選</strong>。
            </p>
          </div>

          <div class="tag-filter-stack">
            <div class="form-group">
              <TagSelectorMultiple
                v-model="form.includeTagIds"
                label="包含標籤"
                hint="會員需具備這些標籤才可集章"
                collapsible
              />
            </div>
            <div class="form-group">
              <TagSelectorMultiple
                v-model="form.excludeTagIds"
                label="排除標籤"
                hint="會員若具備這些標籤則不可集章"
                collapsible
              />
            </div>
          </div>

          <div class="form-row checkbox-row">
            <div class="toggle-option">
              <span class="toggle-option-label">允許人工補章</span>
              <label class="toggle-switch">
                <input v-model="form.allowManualIssue" type="checkbox" />
                <span class="toggle-slider"></span>
              </label>
            </div>
            <div class="toggle-option">
              <span class="toggle-option-label">允許 QR 補充發章</span>
              <label class="toggle-switch">
                <input v-model="form.allowQrIssue" type="checkbox" />
                <span class="toggle-slider"></span>
              </label>
            </div>
          </div>
          <div class="qr-enable-hint" :class="{ 'qr-enable-hint--wixtar': isWixtarClient && !wixtarTransactionStampIssueEnabled }">
            <i :class="isWixtarClient && !wixtarTransactionStampIssueEnabled ? 'ri-qr-code-line' : 'ri-information-line'"></i>
            <div>
              <p v-if="isWixtarClient && !wixtarTransactionStampIssueEnabled">
                尚未開啟 Wixtar 交易觸發集章（請至集章活動列表設定）時，請務必開啟 QR 補章並於「補章／QR Code」分頁取得 QR，供現場掃描發章。
                <span v-if="isEdit && form.allowQrIssue && !savedAllowQrIssue">開啟後會先自動儲存再顯示。</span>
              </p>
              <p v-else>
                開啟後可取得會員掃描用 QR Code，現場掃描即可補章。
                <span v-if="isEdit && form.allowQrIssue && !savedAllowQrIssue">開啟後會先自動儲存再顯示。</span>
              </p>
              <button
                v-if="isEdit && form.allowQrIssue"
                type="button"
                class="btn btn-sm btn-primary"
                :disabled="submitting"
                @click="goToQrIssueTab"
              >
                <i class="ri-qr-code-line"></i>
                {{
                  submitting
                    ? '儲存中...'
                    : savedAllowQrIssue
                      ? '查看 QR Code'
                      : '儲存並查看 QR Code'
                }}
              </button>
            </div>
          </div>
        </div>
        </div>

        <div v-show="!isEdit || activeTab === 'rewards'" class="tab-pane">
        <div class="form-section">
          <div class="section-header">
            <h3 class="section-title">達成目標設定</h3>
            <button type="button" class="btn btn-primary" @click="addGoal">
              <i class="ri-add-line"></i>
              新增達成目標
            </button>
          </div>

          <div v-if="form.goals.length === 0" class="empty-state">
            尚未設定達成目標
          </div>

          <div v-else class="goal-list">
            <div v-for="(goal, goalIndex) in form.goals" :key="goal.uid" class="goal-card">
              <div class="goal-card-header">
                <strong>目標 {{ goalIndex + 1 }}</strong>
                <button type="button" class="btn btn-sm btn-danger" @click="removeGoal(goalIndex)">
                  刪除
                </button>
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label>達成章數</label>
                  <input
                    v-model.number="goal.stampThreshold"
                    type="number"
                    class="form-control"
                    :class="{ 'is-invalid': goal.stampThreshold > form.totalStamps }"
                  />
                  <div v-if="goal.stampThreshold > form.totalStamps" class="invalid-feedback">
                    達成章數不可大於總章數（{{ form.totalStamps }} 章）
                  </div>
                </div>
                <div class="form-group">
                  <label>&nbsp;</label>
                  <button type="button" class="btn btn-secondary" @click="addGoalAction(goal)">
                    <i class="ri-add-line"></i> 新增動作
                  </button>
                </div>
              </div>

              <div v-if="goal.actions.length === 0" class="empty-state">
                此目標尚未設定動作
              </div>
              <div v-else class="goal-action-list">
                <div
                  v-for="(action, actionIndex) in goal.actions"
                  :key="action.uid"
                  class="reward-card"
                >
                  <div class="reward-card-header">
                    <strong>動作 {{ actionIndex + 1 }}</strong>
                    <button
                      type="button"
                      class="btn btn-sm btn-danger"
                      @click="removeGoalAction(goal, actionIndex)"
                    >
                      刪除
                    </button>
                  </div>

                  <div class="form-row">
                    <div class="form-group full-width">
                      <label>動作類型</label>
                      <select v-model="action.rewardType" class="form-control">
                        <option value="point">點數</option>
                        <option value="coupon">優惠券</option>
                        <option value="tag">標籤</option>
                        <option value="message">訊息推播</option>
                        <option v-if="isWixtarClient || action.rewardType === 'wixtar_ticket'" value="wixtar_ticket">Wixtar 票券</option>
                      </select>
                    </div>
                  </div>

                  <div class="form-row" v-if="action.rewardType === 'point'">
                    <div class="form-group">
                      <label>點數</label>
                      <input v-model.number="action.points" type="number" class="form-control" min="1" />
                    </div>
                    <div class="form-group">
                      <label>說明</label>
                      <input v-model="action.description" type="text" class="form-control" placeholder="例如：集章獎勵點數" />
                    </div>
                  </div>

                  <div class="form-row" v-else-if="action.rewardType === 'coupon'">
                    <div class="form-group full-width">
                      <label>優惠券範本</label>
                      <select
                        v-model="action.couponTemplateId"
                        class="form-control"
                        :disabled="couponTemplatesLoading"
                      >
                        <option value="">請選擇優惠券範本</option>
                        <option v-for="t in couponTemplates" :key="t.id" :value="t.id">
                          {{ t.templateName }} — {{ formatCouponTypeText(t.couponType) }} {{ formatDiscountValue(t) }}
                        </option>
                      </select>
                      <RewardStockWarning :template="couponTemplates.find((item) => item.id === action.couponTemplateId)" />
                      <p v-if="couponTemplatesLoading" class="form-hint">載入中...</p>
                      <p v-else-if="!couponTemplates.length" class="form-hint">尚無啟用中的範本，請先建立範本後再選取</p>
                      <div v-if="!couponTemplatesLoading" class="picker-goto-link">
                        <router-link :to="{ name: 'CreateCouponTemplate' }" target="_blank">
                          <i class="ri-add-line"></i> 新增優惠券範本
                        </router-link>
                      </div>
                    </div>
                  </div>

                  <div class="form-row" v-else-if="action.rewardType === 'wixtar_ticket'">
                    <div class="form-group full-width">
                      <label>Wixtar 票券</label>
                      <WixtarTicketMultiSelect
                        :model-value="action.wixtarTicketIds"
                        :tickets="wixtarTickets"
                        :loading="wixtarTicketsLoading"
                        @update:model-value="action.wixtarTicketIds = $event"
                      />
                      <p class="form-hint">可選擇多張票券，會員需已綁定 Wixtar。</p>
                    </div>
                  </div>

                  <div class="form-row" v-else-if="action.rewardType === 'tag'">
                    <div class="form-group full-width">
                      <label>要貼上的標籤</label>
                      <TagSelectorMultiple v-model="action.tagIds" label="" />
                    </div>
                  </div>

                  <template v-else-if="action.rewardType === 'message'">
                    <div class="form-row">
                      <div class="form-group full-width line-msg-picker-block">
                        <label>LINE 訊息範本</label>
                        <LineMsgTemplatePicker
                          :key="`${action.uid}-line-msg`"
                          audience-filter="personal"
                          :model-value="action.lineMsgTemplate"
                          :selected-id="action.templateCode || null"
                          @update:model-value="(t) => onMessageTemplateChange(action, t)"
                        />
                        <div class="picker-goto-link">
                          <router-link :to="{ name: 'LineMsgTemplateCreate' }" target="_blank">
                            <i class="ri-add-line"></i> 新增 LINE 訊息範本
                          </router-link>
                        </div>
                      </div>
                    </div>
                    <div class="form-row">
                      <div class="form-group full-width">
                        <label>訊息內容說明</label>
                        <input v-model="action.description" type="text" class="form-control" placeholder="內部描述" />
                      </div>
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>

        <div v-if="isEdit && activeTab === 'issue'" class="tab-pane">
          <div class="form-section">
            <h3 class="section-title">QR 補章</h3>
            <div v-if="!savedAllowQrIssue" class="empty-state">
              <template v-if="form.allowQrIssue">
                <p>已勾選「允許 QR 補充發章」，但尚未儲存。</p>
                <button
                  type="button"
                  class="btn btn-sm btn-primary"
                  style="margin-top: 12px;"
                  :disabled="submitting"
                  @click="goToQrIssueTab"
                >
                  <i class="ri-qr-code-line"></i>
                  {{ submitting ? '儲存中...' : '儲存並顯示 QR Code' }}
                </button>
              </template>
              <template v-else>
                <p>此活動尚未開放 QR 補章。</p>
                <p class="form-hint">請至「基本設定」勾選「允許 QR 補充發章」。</p>
                <button type="button" class="btn btn-sm btn-primary" style="margin-top: 12px;" @click="activeTab = 'basic'">
                  前往基本設定
                </button>
              </template>
            </div>
            <template v-else>
              <p v-if="isWixtarClient && !wixtarTransactionStampIssueEnabled" class="form-hint">
                目前未開啟 Wixtar 交易觸發集章，請列印或展示此 QR Code 供會員用 LINE 掃描發章。
              </p>
              <p v-else class="form-hint">
                列印或展示此 QR Code，會員用 LINE 掃描後即可領取印章。每次掃描會依活動規則發章。
              </p>
              <div v-if="qrIssueLoading" class="loading-state">
                <div class="spinner"></div>
                <p>載入 QR Code 中...</p>
              </div>
              <div v-else-if="qrIssueInfo.qrImageDataUrl" class="qr-issue-panel">
                <img :src="qrIssueInfo.qrImageDataUrl" alt="QR 補章" class="qr-issue-image" />
                <div class="qr-issue-url">
                  <label>掃描網址</label>
                  <code>{{ qrIssueInfo.scanUrl }}</code>
                  <div class="qr-issue-actions">
                    <button type="button" class="btn btn-sm btn-secondary" @click="copyQrUrl">
                      <i class="ri-file-copy-line"></i> 複製網址
                    </button>
                    <button type="button" class="btn btn-sm btn-primary" @click="downloadQrImage">
                      <i class="ri-download-line"></i> 下載 QR Code
                    </button>
                  </div>
                </div>
              </div>
              <div v-else class="empty-state">
                無法載入 QR Code
                <button type="button" class="btn btn-sm btn-primary" style="margin-top: 12px;" @click="loadQrIssueInfo">
                  重新載入
                </button>
              </div>
            </template>
          </div>

          <div class="form-section">
            <h3 class="section-title">人工補章</h3>
            <div v-if="!form.allowManualIssue" class="empty-state">
              此活動未開放人工補章，請至「基本設定」勾選「允許人工補章」並儲存後再使用。
            </div>
            <template v-else>
              <p class="form-hint">選擇會員後可手動發放印章，適用於客服補登或特殊情況。</p>
              <div class="form-group">
                <label class="required">選擇會員</label>
                <MemberSearch v-model="manualIssueMembers" :max-selections="1" />
              </div>
              <div class="form-row">
                <div class="form-group">
                  <label>發章數</label>
                  <input
                    v-model.number="manualIssueForm.stampCount"
                    type="number"
                    class="form-control"
                    min="1"
                    max="20"
                  />
                  <p class="form-hint">預設為每次發章數（{{ form.stampCount }}）</p>
                </div>
                <div class="form-group">
                  <label>備註</label>
                  <input
                    v-model="manualIssueForm.note"
                    type="text"
                    class="form-control"
                    placeholder="例如：客服補登"
                  />
                </div>
              </div>
              <button
                type="button"
                class="btn btn-primary"
                :disabled="manualIssueSubmitting"
                @click="handleManualIssue"
              >
                <span v-if="manualIssueSubmitting"><i class="ri-loader-4-line spin"></i> 補章中...</span>
                <span v-else><i class="ri-stamp-line"></i> 確認補章</span>
              </button>
            </template>
          </div>
        </div>

        <div v-if="isEdit && activeTab === 'stats'" class="tab-pane">
          <div class="form-section">
            <h3 class="section-title">活動統計</h3>
            <div class="stats-grid">
              <div class="stat-card">
                <div class="stat-label">參與會員數</div>
                <div class="stat-value">{{ statistics.totalParticipants || 0 }}</div>
              </div>
              <div class="stat-card">
                <div class="stat-label">總發章次數</div>
                <div class="stat-value">{{ statistics.totalStampsIssued || 0 }}</div>
              </div>
              <div class="stat-card">
                <div class="stat-label">完成次數</div>
                <div class="stat-value">{{ statistics.completedCount || 0 }}</div>
              </div>
              <div class="stat-card">
                <div class="stat-label">完成率</div>
                <div class="stat-value">{{ statistics.completionRate || 0 }}%</div>
              </div>
            </div>
          </div>

          <div class="form-section">
            <h3 class="section-title">參與會員</h3>
            <div class="page-toolbar">
              <div class="filter-group">
                <input
                  v-model="participantsFilter.search"
                  type="text"
                  class="form-control"
                  placeholder="搜尋會員名稱或手機..."
                  @keyup.enter="fetchParticipants"
                />
              </div>
              <div class="filter-group filter-group--action">
                <button type="button" class="btn btn-primary" @click="fetchParticipants">
                  <i class="ri-search-line"></i> 查詢
                </button>
              </div>
            </div>
            <div v-if="loadingParticipants" class="loading-state">
              <div class="spinner"></div>
              <p>載入中...</p>
            </div>
            <div v-else-if="participants.length === 0" class="empty-state">尚無參與會員</div>
            <div v-else class="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>會員</th>
                    <th>手機</th>
                    <th>目前章數</th>
                    <th>完成次數</th>
                    <th>最後集章</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in participants" :key="item.id">
                    <td>{{ item.memberName || '-' }}</td>
                    <td>{{ item.memberPhone || '-' }}</td>
                    <td>{{ item.currentStampCount || 0 }} / {{ form.totalStamps }}</td>
                    <td>{{ item.completedCount || 0 }}</td>
                    <td>{{ formatDateTime(item.lastStampedAt) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="form-section">
            <h3 class="section-title">發章紀錄</h3>
            <div class="page-toolbar">
              <div class="filter-group">
                <label class="filter-label">來源</label>
                <select v-model="recordsFilter.sourceType" class="form-control">
                  <option value="">全部</option>
                  <option value="order">交易集章</option>
                  <option value="manual">人工補章</option>
                  <option value="qr_scan">QR 掃碼</option>
                  <option value="event">活動事件</option>
                </select>
              </div>
              <div class="filter-group">
                <label class="filter-label">會員</label>
                <input
                  v-model="recordsFilter.search"
                  type="text"
                  class="form-control"
                  placeholder="名稱或手機"
                  @keyup.enter="fetchRecords"
                />
              </div>
              <div class="filter-group">
                <label class="filter-label">日期（起）</label>
                <input v-model="recordsFilter.startDate" type="date" class="form-control" />
              </div>
              <div class="filter-group">
                <label class="filter-label">日期（迄）</label>
                <input v-model="recordsFilter.endDate" type="date" class="form-control" />
              </div>
              <div class="filter-group filter-group--action">
                <button type="button" class="btn btn-primary" @click="fetchRecords">
                  <i class="ri-search-line"></i> 查詢
                </button>
              </div>
            </div>
            <div v-if="loadingRecords" class="loading-state">
              <div class="spinner"></div>
              <p>載入中...</p>
            </div>
            <div v-else-if="records.length === 0" class="empty-state">尚無發章紀錄</div>
            <div v-else class="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>時間</th>
                    <th>會員</th>
                    <th>來源</th>
                    <th>發章數</th>
                    <th>累計章數</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="record in records" :key="record.id">
                    <td>{{ formatDateTime(record.issuedAt) }}</td>
                    <td>{{ record.memberName || record.memberPhone || '-' }}</td>
                    <td>{{ formatSourceType(record.sourceType) }}</td>
                    <td>{{ record.stampCount || 0 }}</td>
                    <td>{{ record.currentStampCount || 0 }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

      </form>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { stampCampaignService } from '@/api/modules/stampCampaigns.js';
import { memberTierService } from '@/api/modules/memberTiers.js';
import { tagService } from '@/api/modules/tags.js';
import { couponService } from '@/api/modules/coupons.js';
import { showError, showSuccess } from '@/utils/swal.js';
import ImageUploader from '@/components/ImageUploader.vue';
import TagSelectorMultiple from '@/components/TagSelectorMultiple.vue';
import LineMsgTemplatePicker from '@/components/LineMsgTemplatePicker.vue';
import MemberSearch from '@/components/MemberSearch.vue';
import WixtarTicketMultiSelect from '@/components/WixtarTicketMultiSelect.vue';
import RewardStockWarning from '@/components/RewardStockWarning.vue';
import { clientService } from '@/api/modules/clients.js';
import { clientSettingsService, NAMESPACES } from '@/api/modules/clientSettings.js';
import { wixtarService } from '@/api/modules/wixtar.js';
import authService from '@/helpers/authservice/authService.js';

let rewardUid = 0;
let goalUid = 0;

const createDefaultAction = () => ({
  uid: `action-${Date.now()}-${rewardUid++}`,
  rewardType: 'point',
  points: 10,
  couponTemplateId: '',
  tagIds: [],
  templateCode: '',
  lineMsgTemplate: null,
  description: '',
  wixtarTicketIds: []
});

const createDefaultGoal = () => ({
  uid: `goal-${Date.now()}-${goalUid++}`,
  stampThreshold: 1,
  actions: [createDefaultAction()]
});

export default {
  name: 'StampCampaignCreate',
  components: {
    ImageUploader,
    TagSelectorMultiple,
    LineMsgTemplatePicker,
    MemberSearch,
    WixtarTicketMultiSelect,
    RewardStockWarning
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const isEdit = computed(() => !!route.params?.id);
    const campaignId = computed(() => route.params?.id || '');
    const activeTab = ref('basic');
    const savedAllowQrIssue = ref(false);
    const submitting = ref(false);
    const memberTiers = ref([]);
    const tagOptions = ref([]);
    const statistics = ref({});
    const couponTemplates = ref([]);
    const couponTemplatesLoading = ref(false);
    const isWixtarClient = ref(false);
    const wixtarTransactionStampIssueEnabled = ref(false);
    const memberSystemLoaded = ref(false);
    const wixtarTickets = ref([]);
    const wixtarTicketsLoading = ref(false);
    const participants = ref([]);
    const records = ref([]);
    const loadingParticipants = ref(false);
    const loadingRecords = ref(false);
    const manualIssueMembers = ref([]);
    const manualIssueSubmitting = ref(false);
    const qrIssueLoading = ref(false);
    const qrIssueInfo = ref({ scanUrl: '', qrImageDataUrl: '' });

    const manualIssueForm = reactive({
      stampCount: 1,
      note: ''
    });

    const participantsFilter = reactive({
      search: ''
    });

    const recordsFilter = reactive({
      search: '',
      sourceType: '',
      startDate: '',
      endDate: ''
    });

    const canSaveCurrentTab = computed(() => !isEdit.value || ['basic', 'rewards'].includes(activeTab.value));
    const allMemberTierCodes = computed(() =>
      memberTiers.value
        .map((tier) => tier.tierCode)
        .filter((code) => Boolean(code))
    );

    const form = reactive({
      campaignCode: '',
      campaignName: '',
      description: '',
      imageUrl: '',
      startAt: '',
      endAt: '',
      totalStamps: 10,
      stampCompletionMode: 'once',
      isActive: true,
      ruleType: 'order_once',
      stampCount: 1,
      minOrderAmount: 0,
      dailyMemberLimit: 0,
      totalIssueLimit: 0,
      memberTierCodes: [],
      includeTagIds: [],
      excludeTagIds: [],
      allowManualIssue: true,
      allowQrIssue: false,
      goals: [createDefaultGoal()]
    });

    const repeatCycleAfterComplete = computed({
      get: () => form.stampCompletionMode === 'repeat_cycle',
      set: (val) => {
        form.stampCompletionMode = val ? 'repeat_cycle' : 'once';
      }
    });

    const errors = reactive({
      campaignCode: '',
      campaignName: '',
      endAt: ''
    });

    const resetErrors = () => {
      Object.keys(errors).forEach((key) => {
        errors[key] = '';
      });
    };

    const validateForm = () => {
      resetErrors();
      let valid = true;

      if (!form.campaignName || !form.campaignName.trim()) {
        errors.campaignName = '活動名稱為必填欄位';
        valid = false;
        if (isEdit.value) activeTab.value = 'basic';
      }

      if (form.ruleType === 'order_amount' && (!form.minOrderAmount || form.minOrderAmount <= 0)) {
        errors.endAt = '滿額規則需設定最低交易金額';
        valid = false;
        if (isEdit.value) activeTab.value = 'basic';
      }

      if (form.startAt && form.endAt) {
        const start = new Date(form.startAt);
        const end = new Date(form.endAt);
        if (end <= start) {
          errors.endAt = '結束時間必須晚於開始時間';
          valid = false;
          if (isEdit.value) activeTab.value = 'basic';
        }
      }

      if (!valid) {
        return false;
      }

      if (memberTiers.value.length === 0) {
        showError('驗證失敗', '尚無會員等級可設定，請先至會員等級管理建立至少一個等級');
        if (isEdit.value) activeTab.value = 'basic';
        return false;
      }

      if (!Array.isArray(form.memberTierCodes) || form.memberTierCodes.length === 0) {
        showError('驗證失敗', '請至少勾選一個支援的會員等級');
        if (isEdit.value) activeTab.value = 'basic';
        return false;
      }

      if (!form.goals.length) {
        showError('驗證失敗', '至少需要設定一個達成目標');
        if (isEdit.value) activeTab.value = 'rewards';
        return false;
      }

      const invalidGoal = form.goals.find((goal) => {
        const threshold = Number(goal.stampThreshold);
        return !Number.isInteger(threshold) || threshold < 1 || threshold > form.totalStamps;
      });
      if (invalidGoal) {
        showError('驗證失敗', `目標達成章數必須介於 1 到 ${form.totalStamps} 章（總章數）之間`);
        if (isEdit.value) activeTab.value = 'rewards';
        return false;
      }

      const duplicateThresholds = new Set();
      const hasDuplicateThreshold = form.goals.some((goal) => {
        const threshold = Number(goal.stampThreshold);
        if (duplicateThresholds.has(threshold)) {
          return true;
        }
        duplicateThresholds.add(threshold);
        return false;
      });
      if (hasDuplicateThreshold) {
        showError('驗證失敗', '每個達成目標的章數需唯一，請調整重複的目標章數');
        if (isEdit.value) activeTab.value = 'rewards';
        return false;
      }

      for (const goal of form.goals) {
        if (!Array.isArray(goal.actions) || goal.actions.length === 0) {
          showError('驗證失敗', `達成 ${goal.stampThreshold} 章至少需要設定一個動作`);
          if (isEdit.value) activeTab.value = 'rewards';
          return false;
        }

        const messageWithoutTemplate = goal.actions.find(
          (action) => action.rewardType === 'message' && !String(action.templateCode || '').trim()
        );
        if (messageWithoutTemplate) {
          showError('驗證失敗', `達成 ${goal.stampThreshold} 章的「訊息推播」動作尚未選擇 LINE 訊息範本`);
          if (isEdit.value) activeTab.value = 'rewards';
          return false;
        }

        const couponWithoutTemplate = goal.actions.find(
          (action) => action.rewardType === 'coupon' && !String(action.couponTemplateId || '').trim()
        );
        if (couponWithoutTemplate) {
          showError('驗證失敗', `達成 ${goal.stampThreshold} 章的「優惠券」動作尚未選擇優惠券範本`);
          if (isEdit.value) activeTab.value = 'rewards';
          return false;
        }

        const invalidPoint = goal.actions.find(
          (action) => action.rewardType === 'point' && Number(action.points || 0) <= 0
        );
        if (invalidPoint) {
          showError('驗證失敗', `達成 ${goal.stampThreshold} 章的「點數」動作需設定大於 0 的點數`);
          if (isEdit.value) activeTab.value = 'rewards';
          return false;
        }

        const wixtarWithoutTickets = goal.actions.find(
          (action) => action.rewardType === 'wixtar_ticket' && !(action.wixtarTicketIds || []).length
        );
        if (wixtarWithoutTickets) {
          showError('驗證失敗', `達成 ${goal.stampThreshold} 章的「Wixtar 票券」動作尚未選擇票券`);
          if (isEdit.value) activeTab.value = 'rewards';
          return false;
        }

        const invalidTagAction = goal.actions.find(
          (action) => action.rewardType === 'tag' && (!Array.isArray(action.tagIds) || action.tagIds.length === 0)
        );
        if (invalidTagAction) {
          showError('驗證失敗', `達成 ${goal.stampThreshold} 章的「標籤」動作至少要選擇一個標籤`);
          if (isEdit.value) activeTab.value = 'rewards';
          return false;
        }
      }

      return valid;
    };

    const toDateTimeLocal = (value) => {
      if (!value) return '';
      const date = new Date(value);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    const mapTagNamesToIds = (tagNames = []) => {
      const optionMap = new Map(tagOptions.value.map(tag => [tag.tagName, tag.id]));
      return tagNames
        .map(name => optionMap.get(name))
        .filter(Boolean);
    };

    const mapTagIdsToNames = (tagIds = []) => {
      const optionMap = new Map(tagOptions.value.map(tag => [tag.id, tag.tagName]));
      return tagIds
        .map(id => optionMap.get(id))
        .filter(Boolean);
    };

    const formatDateTime = (value) => {
      if (!value) return '-';
      return new Date(value).toLocaleString('zh-TW');
    };

    const formatSourceType = (sourceType) => {
      const map = {
        order: '交易集章',
        manual: '人工補章',
        qr_scan: 'QR 掃碼',
        event: '活動事件'
      };
      return map[sourceType] || sourceType || '-';
    };

    const fetchStatistics = async () => {
      if (!isEdit.value) return;
      try {
        const response = await stampCampaignService.getStatistics(campaignId.value);
        if (response?.success) {
          statistics.value = response.data || {};
        }
      } catch (err) {
        console.error('載入統計失敗:', err);
      }
    };

    const fetchParticipants = async () => {
      if (!isEdit.value) return;
      loadingParticipants.value = true;
      try {
        const params = { pageSize: 50 };
        if (participantsFilter.search) {
          params.search = participantsFilter.search.trim();
        }
        const response = await stampCampaignService.getParticipants(campaignId.value, params);
        if (response?.success) {
          participants.value = response.data?.items || [];
        }
      } catch (err) {
        console.error('載入參與會員失敗:', err);
      } finally {
        loadingParticipants.value = false;
      }
    };

    const fetchRecords = async () => {
      if (!isEdit.value) return;
      loadingRecords.value = true;
      try {
        const params = { pageSize: 50 };
        if (recordsFilter.search) params.search = recordsFilter.search.trim();
        if (recordsFilter.sourceType) params.sourceType = recordsFilter.sourceType;
        if (recordsFilter.startDate) params.startDate = recordsFilter.startDate;
        if (recordsFilter.endDate) params.endDate = recordsFilter.endDate;
        const response = await stampCampaignService.getRecords(campaignId.value, params);
        if (response?.success) {
          records.value = response.data?.items || [];
        }
      } catch (err) {
        console.error('載入發章紀錄失敗:', err);
      } finally {
        loadingRecords.value = false;
      }
    };

    const loadQrIssueInfo = async () => {
      if (!isEdit.value || !savedAllowQrIssue.value) return;
      qrIssueLoading.value = true;
      try {
        const response = await stampCampaignService.getQrIssueInfo(campaignId.value);
        if (response?.success) {
          qrIssueInfo.value = response.data || {};
        }
      } catch (err) {
        console.error('載入 QR 補章資訊失敗:', err);
        await showError('載入失敗', err.message || '無法載入 QR Code');
      } finally {
        qrIssueLoading.value = false;
      }
    };

    const persistCampaign = async ({ silent = false, switchToIssue = false } = {}) => {
      if (!validateForm()) return false;

      submitting.value = true;
      try {
        const payload = buildPayload();
        const response = isEdit.value
          ? await stampCampaignService.updateCampaign(campaignId.value, payload)
          : await stampCampaignService.createCampaign(payload);

        if (!response?.success) {
          await showError(isEdit.value ? '更新失敗' : '建立失敗', response?.message || '操作失敗');
          return false;
        }

        if (!silent) {
          await showSuccess(isEdit.value ? '更新成功' : '建立成功', `集章活動已${isEdit.value ? '更新' : '建立'}`);
        }

        if (isEdit.value) {
          await loadCampaign();
          if (switchToIssue) {
            activeTab.value = 'issue';
          }
        } else {
          router.push({
            name: 'EditStampCampaign',
            params: { id: response.data.id },
            query: switchToIssue || form.allowQrIssue ? { tab: 'issue' } : undefined
          });
        }
        return true;
      } catch (err) {
        console.error('儲存集章活動失敗:', err);
        await showError('儲存失敗', err.message || '儲存集章活動失敗');
        return false;
      } finally {
        submitting.value = false;
      }
    };

    const goToQrIssueTab = async () => {
      if (!form.allowQrIssue) {
        activeTab.value = 'basic';
        return;
      }
      if (!savedAllowQrIssue.value) {
        const saved = await persistCampaign({ silent: true, switchToIssue: true });
        if (!saved) return;
        await loadQrIssueInfo();
        await showSuccess('已儲存', '已開啟 QR 補章，可直接使用下方 QR Code');
        return;
      }
      activeTab.value = 'issue';
      await loadQrIssueInfo();
    };

    const copyQrUrl = async () => {
      if (!qrIssueInfo.value.scanUrl) return;
      try {
        await navigator.clipboard.writeText(qrIssueInfo.value.scanUrl);
        await showSuccess('已複製', '掃描網址已複製到剪貼簿');
      } catch (err) {
        await showError('複製失敗', '無法複製網址');
      }
    };

    const downloadQrImage = () => {
      if (!qrIssueInfo.value.qrImageDataUrl) return;
      const link = document.createElement('a');
      link.href = qrIssueInfo.value.qrImageDataUrl;
      link.download = `stamp-qr-${form.campaignCode || campaignId.value || 'campaign'}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    const handleManualIssue = async () => {
      if (!manualIssueMembers.value.length) {
        await showError('驗證失敗', '請先選擇會員');
        return;
      }
      if (manualIssueMembers.value.length > 1) {
        await showError('驗證失敗', '人工補章一次僅能選擇一位會員');
        return;
      }

      manualIssueSubmitting.value = true;
      try {
        const member = manualIssueMembers.value[0];
        const response = await stampCampaignService.manualIssue(campaignId.value, {
          memberId: member.id,
          stampCount: manualIssueForm.stampCount || form.stampCount || 1,
          note: manualIssueForm.note || null
        });
        if (response?.success) {
          await showSuccess('補章成功', '已為會員發放印章');
          manualIssueMembers.value = [];
          manualIssueForm.note = '';
          await Promise.all([fetchStatistics(), fetchParticipants(), fetchRecords()]);
        } else {
          await showError('補章失敗', response?.message || '操作失敗');
        }
      } catch (err) {
        console.error('人工補章失敗:', err);
        await showError('補章失敗', err.message || '人工補章失敗');
      } finally {
        manualIssueSubmitting.value = false;
      }
    };

    const formatCouponTypeText = (type) => {
      const typeMap = {
        discount: '折扣券',
        fixed_amount: '折價券',
        free_shipping: '運費券',
        gift: '贈品券'
      };
      return typeMap[type] || type || '';
    };

    const formatDiscountValue = (template) => {
      if (!template) return '';
      if (template.couponType === 'discount') {
        return `${template.discountValue}%`;
      }
      if (template.couponType === 'fixed_amount') {
        return `$${Number(template.discountValue).toLocaleString('zh-TW')}`;
      }
      if (template.couponType === 'free_shipping') {
        return '免運費';
      }
      if (template.couponType === 'gift') {
        return '贈品';
      }
      return '-';
    };

    const loadCouponTemplates = async () => {
      couponTemplatesLoading.value = true;
      try {
        const response = await couponService.getTemplates({ pageSize: 200, isActive: true });
        if (response?.success && response.data) {
          couponTemplates.value = response.data.items || [];
        }
      } catch (err) {
        console.error('載入優惠券範本失敗:', err);
      } finally {
        couponTemplatesLoading.value = false;
      }
    };

    const buildPayload = () => {
      const rewards = form.goals
        .flatMap((goal) => {
          const stampThreshold = Number(goal.stampThreshold || 0);
          return (goal.actions || [])
            .filter((action) => !(memberSystemLoaded.value && !isWixtarClient.value && action.rewardType === 'wixtar_ticket'))
            .map((action) => ({
            stampThreshold,
            rewardType: action.rewardType,
            rewardConfig: {
              points: action.rewardType === 'point' ? Number(action.points || 0) : undefined,
              couponTemplateId: action.rewardType === 'coupon' ? action.couponTemplateId || null : undefined,
              tagIds: action.rewardType === 'tag' ? action.tagIds || [] : undefined,
              templateCode:
                action.rewardType === 'message'
                  ? action.templateCode ||
                    (action.lineMsgTemplate &&
                      (action.lineMsgTemplate.templateCode || action.lineMsgTemplate.id)) ||
                    null
                  : undefined,
              description: action.description || null,
              wixtarTicketIds: action.rewardType === 'wixtar_ticket' ? action.wixtarTicketIds || [] : undefined
            },
            isActive: true
          }));
        })
        .sort((a, b) => a.stampThreshold - b.stampThreshold);

      return {
        campaignCode: form.campaignCode || null,
        campaignName: form.campaignName.trim(),
        description: form.description ? form.description.trim() : null,
        imageUrl: form.imageUrl || null,
        startAt: form.startAt ? new Date(form.startAt).toISOString() : null,
        endAt: form.endAt ? new Date(form.endAt).toISOString() : null,
        totalStamps: form.totalStamps,
        allowRepeatCompletion: form.stampCompletionMode === 'repeat_cycle',
        resetOnComplete: form.stampCompletionMode === 'repeat_cycle',
        isActive: !!form.isActive,
        ruleConfig: {
          ruleType: form.ruleType,
          stampCount: form.stampCount,
          minOrderAmount: form.ruleType === 'order_amount' ? Number(form.minOrderAmount || 0) : 0,
          dailyMemberLimit: Number(form.dailyMemberLimit || 0),
          totalIssueLimit: Number(form.totalIssueLimit || 0),
          allowManualIssue: form.allowManualIssue,
          allowQrIssue: form.allowQrIssue
        },
        allowedTierCodes: Array.isArray(form.memberTierCodes) ? form.memberTierCodes : [],
        requiredTagIds: form.includeTagIds,
        excludedTagIds: form.excludeTagIds,
        rewards
      };
    };

    const loadMemberTiers = async () => {
      try {
        const response = await memberTierService.getTiers({ isActive: true });
        if (response?.success) {
          const raw = response.data;
          memberTiers.value = Array.isArray(raw) ? raw : raw?.items || [];
          if (!isEdit.value && form.memberTierCodes.length === 0) {
            form.memberTierCodes = [...allMemberTierCodes.value];
          }
        }
      } catch (err) {
        console.error('載入會員等級失敗:', err);
      }
    };

    const loadTagOptions = async () => {
      try {
        const response = await tagService.getTags({ isActive: true });
        if (response?.success) {
          tagOptions.value = response.data || [];
        }
      } catch (err) {
        console.error('預載標籤失敗:', err);
      }
    };

    const loadCampaign = async () => {
      if (!isEdit.value) return;
      try {
        const [campaignResponse, statsResponse] = await Promise.all([
          stampCampaignService.getCampaign(campaignId.value),
          stampCampaignService.getStatistics(campaignId.value)
        ]);

        if (campaignResponse?.success && campaignResponse.data) {
          const campaign = campaignResponse.data;
          form.campaignCode = campaign.campaignCode || '';
          form.campaignName = campaign.campaignName || '';
          form.description = campaign.description || '';
          form.imageUrl = campaign.imageUrl || '';
          form.startAt = toDateTimeLocal(campaign.startAt);
          form.endAt = toDateTimeLocal(campaign.endAt);
          form.totalStamps = campaign.totalStamps || 10;
          form.stampCompletionMode =
            campaign.allowRepeatCompletion && campaign.resetOnComplete ? 'repeat_cycle' : 'once';
          form.isActive = !!campaign.isActive;

          const ruleConfig = campaign.ruleConfig || {};
          form.ruleType = ruleConfig.ruleType || 'order_once';
          form.stampCount = ruleConfig.stampCount || 1;
          form.minOrderAmount = ruleConfig.minOrderAmount || 0;
          form.dailyMemberLimit = ruleConfig.dailyMemberLimit || 0;
          form.totalIssueLimit = ruleConfig.totalIssueLimit || 0;
          const allTierCodes = [...allMemberTierCodes.value];
          const validTierCodeSet = new Set(allTierCodes);
          const codes = Array.isArray(campaign.allowedTierCodes)
            ? campaign.allowedTierCodes.filter((code) => validTierCodeSet.has(code))
            : [];
          form.memberTierCodes = codes.length > 0 ? [...codes] : allTierCodes;
          form.includeTagIds = campaign.requiredTagIds || [];
          form.excludeTagIds = campaign.excludedTagIds || [];
          form.allowManualIssue = ruleConfig.allowManualIssue !== false;
          form.allowQrIssue = !!ruleConfig.allowQrIssue;
          savedAllowQrIssue.value = !!ruleConfig.allowQrIssue;
          manualIssueForm.stampCount = ruleConfig.stampCount || 1;

          const rewardRows = (campaign.rewards || []).map((reward) => {
            const cfg = reward.rewardConfig || {};
            const wasOther = reward.rewardType === 'other';
            const rewardType = wasOther ? 'point' : reward.rewardType;
            const pointsForRow =
              rewardType === 'point'
                ? wasOther
                  ? Number(cfg.points) > 0
                    ? Number(cfg.points)
                    : 10
                  : cfg.points || 0
                : cfg.points || 0;
            return {
              stampThreshold: Number(reward.stampThreshold || 1),
              action: {
                uid: `action-${reward.id || rewardUid++}`,
                rewardType,
                points: pointsForRow,
                couponTemplateId: cfg.couponTemplateId || '',
                tagIds: cfg.tagIds || [],
                templateCode: cfg.templateCode || '',
                lineMsgTemplate: null,
                description: cfg.description || '',
                wixtarTicketIds: cfg.wixtarTicketIds || []
              }
            };
          });

          const goalMap = new Map();
          for (const row of rewardRows) {
            const key = row.stampThreshold;
            if (!goalMap.has(key)) {
              goalMap.set(key, {
                uid: `goal-${key}-${goalUid++}`,
                stampThreshold: key,
                actions: []
              });
            }
            goalMap.get(key).actions.push(row.action);
          }

          form.goals = Array.from(goalMap.values()).sort((a, b) => a.stampThreshold - b.stampThreshold);
          if (!form.goals.length) {
            form.goals = [createDefaultGoal()];
          }
        }

        if (statsResponse?.success) {
          statistics.value = statsResponse.data || {};
        }
      } catch (err) {
        console.error('載入集章活動失敗:', err);
        await showError('載入失敗', err.message || '無法載入集章活動');
        router.push({ name: 'StampCampaignList' });
      }
    };

    const handleSubmit = async () => {
      await persistCampaign({
        silent: false,
        switchToIssue: isEdit.value && form.allowQrIssue && !savedAllowQrIssue.value
      });
    };

    const addGoal = () => {
      form.goals.push(createDefaultGoal());
    };

    const removeGoal = (index) => {
      if (form.goals.length === 1) {
        showError('無法刪除', '至少需要保留一個達成目標');
        return;
      }
      form.goals.splice(index, 1);
    };

    const addGoalAction = (goal) => {
      goal.actions.push(createDefaultAction());
    };

    const removeGoalAction = (goal, actionIndex) => {
      if (goal.actions.length === 1) {
        showError('無法刪除', '每個達成目標至少需要保留一個動作');
        return;
      }
      goal.actions.splice(actionIndex, 1);
    };

    const toggleTier = (tierCode) => {
      const idx = form.memberTierCodes.indexOf(tierCode);
      if (idx === -1) {
        form.memberTierCodes.push(tierCode);
      } else {
        form.memberTierCodes.splice(idx, 1);
      }
    };

    const selectAllTiers = () => {
      form.memberTierCodes = [...allMemberTierCodes.value];
    };

    const clearAllTiers = () => {
      form.memberTierCodes = [];
    };

    const handleCancel = () => {
      router.push({ name: 'StampCampaignList' });
    };

    const uploadImage = async (file) => {
      return stampCampaignService.uploadImage(file);
    };

    const onMessageTemplateChange = (action, template) => {
      action.lineMsgTemplate = template;
      action.templateCode = template ? template.templateCode || template.id : '';
    };

    watch(activeTab, async (tab) => {
      if (!isEdit.value) return;
      if (tab === 'stats') {
        await Promise.all([fetchStatistics(), fetchParticipants(), fetchRecords()]);
      }
      if (tab === 'issue' && savedAllowQrIssue.value) {
        await loadQrIssueInfo();
      }
    });

    watch(
      () => tagOptions.value,
      (options) => {
        if (!options.length) return;
        form.includeTagIds = [...form.includeTagIds];
        form.excludeTagIds = [...form.excludeTagIds];
      }
    );

    const loadWixtarStampIssueSetting = async () => {
      try {
        const [wixtarRes, tierRes] = await Promise.all([
          clientSettingsService.get(NAMESPACES.WIXTAR),
          clientSettingsService.get(NAMESPACES.MEMBER_TIER)
        ]);
        const wixtar = wixtarRes?.data?.settings ?? {};
        const tier = tierRes?.data?.settings ?? {};
        wixtarTransactionStampIssueEnabled.value = !!(
          wixtar.wixtarTransactionStampIssueEnabled || tier.wixtarTransactionStampIssueEnabled
        );
      } catch (err) {
        console.error('載入 Wixtar 交易集章設定失敗:', err);
        wixtarTransactionStampIssueEnabled.value = false;
      }
    };

    const loadWixtarContext = async () => {
      const user = authService.getCurrentUser();
      if (!user?.clientId) {
        memberSystemLoaded.value = true;
        return;
      }
      try {
        const res = await clientService.getClient(user.clientId);
        isWixtarClient.value = res?.success && res.data?.clientData?.memberSystemType === 'WIXTAR';
        memberSystemLoaded.value = true;
      } catch {
        memberSystemLoaded.value = false;
        return;
      }
      if (!isWixtarClient.value) return;
      await loadWixtarStampIssueSetting();
      wixtarTicketsLoading.value = true;
      try {
        wixtarTickets.value = await wixtarService.listAllTicketCatalog();
      } catch (err) {
        console.error('載入 Wixtar 票券失敗:', err);
        wixtarTickets.value = [];
      } finally {
        wixtarTicketsLoading.value = false;
      }
    };

    onMounted(async () => {
      await Promise.all([loadMemberTiers(), loadTagOptions(), loadCouponTemplates(), loadWixtarContext()]);
      await loadCampaign();
      if (isEdit.value && route.query.tab === 'issue') {
        activeTab.value = 'issue';
      }
    });

    return {
      isEdit,
      activeTab,
      savedAllowQrIssue,
      canSaveCurrentTab,
      form,
      repeatCycleAfterComplete,
      errors,
      memberTiers,
      allMemberTierCodes,
      tagOptions,
      statistics,
      participants,
      records,
      participantsFilter,
      recordsFilter,
      loadingParticipants,
      loadingRecords,
      manualIssueMembers,
      manualIssueForm,
      manualIssueSubmitting,
      qrIssueInfo,
      qrIssueLoading,
      goToQrIssueTab,
      copyQrUrl,
      downloadQrImage,
      loadQrIssueInfo,
      couponTemplates,
      couponTemplatesLoading,
      isWixtarClient,
      wixtarTransactionStampIssueEnabled,
      wixtarTickets,
      wixtarTicketsLoading,
      formatCouponTypeText,
      formatDiscountValue,
      formatDateTime,
      formatSourceType,
      submitting,
      handleSubmit,
      handleCancel,
      handleManualIssue,
      fetchParticipants,
      fetchRecords,
      addGoal,
      removeGoal,
      addGoalAction,
      removeGoalAction,
      uploadImage,
      toggleTier,
      selectAllTiers,
      clearAllTiers,
      onMessageTemplateChange
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_form-common.scss';
@import '~@/assets/scss/components/_list-page-common.scss';

.tab-nav {
  display: flex;
  gap: 4px;
  margin-bottom: 20px;
  border-bottom: 2px solid #e5e7eb;
  flex-wrap: wrap;
}

.tab-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  cursor: pointer;
}

.tab-btn:hover {
  color: #2563eb;
}

.tab-btn.active {
  color: #2563eb;
  border-bottom-color: #2563eb;
}

.tab-pane {
  margin-bottom: 8px;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px;
  color: #64748b;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e5e7eb;
  border-top-color: #2563eb;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.qr-enable-hint {
  display: flex;
  gap: 10px;
  margin-top: 12px;
  padding: 12px 14px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  color: #475569;
  font-size: 13px;
  line-height: 1.5;
}

.qr-enable-hint--wixtar {
  background: #fff8e6;
  border-color: #f0d78c;
  color: #7a5b00;
}

.qr-enable-hint > i {
  flex-shrink: 0;
  margin-top: 2px;
  color: #2563eb;
  font-size: 16px;
}

.qr-enable-hint--wixtar > i {
  color: #c98a00;
}

.qr-enable-hint p {
  margin: 0 0 8px;
}

.qr-enable-hint p:last-child {
  margin-bottom: 0;
}

.qr-issue-panel {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  align-items: flex-start;
  padding: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fafafa;
}

.qr-issue-image {
  width: 220px;
  height: 220px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
}

.qr-issue-url {
  flex: 1;
  min-width: 240px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.qr-issue-url code {
  display: block;
  word-break: break-all;
  padding: 10px 12px;
  background: #f1f5f9;
  border-radius: 6px;
  font-size: 13px;
}

.qr-issue-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.table-wrapper table {
  min-width: 720px;
}

.filter-label {
  display: block;
  font-size: 12px;
  color: #64748b;
  margin-bottom: 4px;
}

.filter-group--action {
  align-self: flex-end;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.tag-filter-stack {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.tag-filter-stack .form-group {
  margin-bottom: 0;
}

.checkbox-row {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
}

.toggle-option {
  display: flex;
  align-items: center;
  gap: 10px;
}

.toggle-option-label {
  font-size: 14px;
  color: #374151;
}

.toggle-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  flex-shrink: 0;
}

.toggle-switch.disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background: #ccc;
  border-radius: 24px;
  transition: background 0.2s;
}

.toggle-slider::before {
  content: '';
  position: absolute;
  width: 18px;
  height: 18px;
  left: 3px;
  top: 3px;
  background: #fff;
  border-radius: 50%;
  transition: transform 0.2s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.toggle-switch input:checked + .toggle-slider {
  background: #4caf50;
}

.toggle-switch input:checked + .toggle-slider::before {
  transform: translateX(20px);
}

.toggle-status {
  font-size: 13px;
  font-weight: 500;
}

.toggle-status.on {
  color: #4caf50;
}

.toggle-status.off {
  color: #9ca3af;
}

.tier-chip-group {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding: 12px 0 4px;
}

.tier-actions {
  display: flex;
  gap: 8px;
  margin-top: 6px;
}

.tier-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px;
  border-radius: 999px;
  border: 1.5px solid #d1d5db;
  background: #f9fafb;
  color: #374151;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  user-select: none;
}

.tier-chip:hover {
  border-color: #2563eb;
  background: #eff6ff;
  color: #2563eb;
}

.tier-chip--active {
  border-color: #2563eb;
  background: #2563eb;
  color: #fff;
}

.tier-chip--active:hover {
  background: #1d4ed8;
  border-color: #1d4ed8;
  color: #fff;
}

.tier-chip-icon {
  font-size: 15px;
}

/* 集滿章卡後 — 模式選擇 */
.completion-mode-group {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  padding: 8px 0 4px;
}

.completion-mode-chip {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  min-width: 170px;
  padding: 14px 18px 14px 14px;
  border-radius: 10px;
  border: 1.5px solid #d1d5db;
  background: #f9fafb;
  color: #374151;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s ease;
}

.completion-mode-chip > i {
  font-size: 18px;
  margin-bottom: 4px;
  color: #9ca3af;
}

.completion-mode-chip:hover {
  border-color: #2563eb;
  background: #eff6ff;
}

.completion-mode-chip:hover > i {
  color: #2563eb;
}

.completion-mode-chip--active {
  border-color: #2563eb;
  background: #eff6ff;
}

.completion-mode-chip--active > i {
  color: #2563eb;
}

.completion-mode-title {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  line-height: 1.3;
}

.completion-mode-chip--active .completion-mode-title {
  color: #1d4ed8;
}

.completion-mode-desc {
  font-size: 12px;
  color: #6b7280;
  line-height: 1.4;
}

.completion-mode-chip--active .completion-mode-desc {
  color: #3b82f6;
}

.goal-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.goal-card {
  border: 1px solid #dbeafe;
  border-radius: 8px;
  padding: 16px;
  background: #f8fbff;
}

.goal-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.goal-action-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 8px;
}

.reward-card {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  background: #fafafa;
}

.reward-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.full-width {
  flex: 1 1 100%;
}

.line-msg-picker-block {
  min-width: 0;
}

.picker-goto-link {
  margin-top: 8px;
  font-size: 12px;
}

.picker-goto-link a {
  color: #3b82f6;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 3px;
}

.picker-goto-link a:hover {
  text-decoration: underline;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
}

.stat-card {
  background: #f8fafc;
  border-radius: 8px;
  padding: 16px;
  text-align: center;
}

.stat-label {
  color: #64748b;
  font-size: 14px;
}

.stat-value {
  margin-top: 8px;
  font-size: 28px;
  font-weight: 700;
  color: #0f172a;
}

.empty-state {
  padding: 24px;
  color: #64748b;
  text-align: center;
  border: 1px dashed #d1d5db;
  border-radius: 8px;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
</style>
